// App.jsx
import React, { useState, useEffect, useCallback, startTransition } from "react";
import {
  Container, Typography, Box, Button, CssBaseline, ThemeProvider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import axios from "axios";

import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import Header from "./header";
import LoginRegisterForm from "./components/LoginRegisterForm";
import DeviceList from "./components/DeviceList";
import KeyModal from "./components/KeyModal";
import FeedbackDialog from "./components/FeedbackDialog";
import ConfirmDialog from "./components/ConfirmDialog";
import AdminDeviceDashboard from "./components/AdminDeviceDashboard";
import ResetPasswordPage from "./components/ResetPasswordPage";
import VerifyEmailPage from "./components/VerifyEmailPage";
import UserGuide from "./components/UserGuide";
import "./app.css";

const theme = createTheme({
  palette: {
    primary: { main: "#3B80DF" },
    secondary: { main: "#BAD1CE" },
    background: { default: "#ffffff", paper: "#ffffff" },
    text: { primary: "#2A2B2E" },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600, color: "#BAD1CE" },
    h6: { color: "#BAD1CE" },
    body1: { color: "#2A2B2E" },
  },
});

const decodeTokenPayload = (token) => {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    let binaryString = "";
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      binaryString = window.atob(base64);
    } else if (typeof Buffer !== "undefined") {
      binaryString = Buffer.from(base64, "base64").toString("binary");
    }

    if (!binaryString) {
      return null;
    }

    const jsonPayload = decodeURIComponent(
      binaryString
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode auth token", error);
    return null;
  }
};

function App() {
  const { t, i18n } = useTranslation();
  const serverUrl = "https://doors.smart-homes.cz";
  //const serverUrl = "http://localhost:8888";

  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [userInfo, setUserInfo] = useState(() =>
    decodeTokenPayload(localStorage.getItem("token"))
  );
  const [devices, setDevices] = useState([]);
  const [keys, setKeys] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: () => {} });
  const [publicSettings, setPublicSettings] = useState({
    app_base_url: "",
    require_email_verification: true,
    smtp_configured: false,
    ldap_enabled: false,
  });

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    setToken("");
    setUserInfo(null);
    setDevices([]);
    setKeys([]);
    setSelectedDevice(null);
    setSelectedDeviceName(null);
  }, [
    setDevices,
    setKeys,
    setSelectedDevice,
    setSelectedDeviceName,
    setToken,
    setUserInfo,
  ]);

  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.setItem("sessionExpired", "1");
    handleLogout();
    showMessage(t("expired"), { clearSession: true });
  }, [handleLogout, t]);

  useEffect(() => {
    if (!token) {
      setUserInfo(null);
      return;
    }

    const decoded = decodeTokenPayload(token);
    if (decoded) {
      setUserInfo(decoded);
      return;
    }

    // Token is invalid or expired, clear stored value
    handleLogout();
  }, [handleLogout, token]);

  useEffect(() => {
    if (localStorage.getItem("sessionExpired")) {
      localStorage.removeItem("sessionExpired");
      setFeedbackMessage(t("expired"));
      setFeedbackOpen(true);
    }

    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          handleSessionExpired();
          showMessage(t("expired"), { clearSession: true });
        }
        return Promise.reject(err);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [handleSessionExpired, t]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${serverUrl}/public/settings`)
      .then((res) => {
        if (cancelled) {
          return;
        }
        const payload = res?.data?.settings;
        if (payload) {
          setPublicSettings((prev) => ({ ...prev, ...payload }));
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Unable to load public settings", error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [serverUrl]);

  const showMessage = (message, opts = {}) => {
    if (opts.clearSession) {
      localStorage.removeItem("sessionExpired");
      localStorage.removeItem("token");
    }
    setFeedbackMessage(message);
    setFeedbackOpen(true);
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const sharedProps = {
    token,
    userInfo,
    setToken,
    devices,
    setDevices,
    setSelectedDevice,
    setSelectedDeviceName,
    setOpenModal,
    handleLogout,
    showMessage,
    serverUrl,
    publicSettings,
  };

  const handleLanguageChange = (lang) =>
    startTransition(() => i18n.changeLanguage(lang));

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <HashRouter>
          <CssBaseline />
          <Header />
          <AppContent
            {...sharedProps}
            t={t}
            onLanguageChange={handleLanguageChange}
          />
          <KeyModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            selectedDevice={selectedDevice}
            selectedDeviceName={selectedDeviceName}
            keys={keys}
            setKeys={setKeys}
            newKey={newKey}
            setNewKey={setNewKey}
            validFrom={validFrom}
            setValidFrom={setValidFrom}
            validTo={validTo}
            setValidTo={setValidTo}
          token={token}
          showMessage={showMessage}
          showConfirm={showConfirm}
          serverUrl={serverUrl}
          devices={devices}
          setDevices={setDevices}
        />
          <FeedbackDialog
            open={feedbackOpen}
            message={feedbackMessage}
            onClose={() => setFeedbackOpen(false)}
          />
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            onCancel={() =>
              setConfirmDialog({ ...confirmDialog, open: false })
            }
            onConfirm={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
          />
          <footer style={{ textAlign: "center", marginTop: 24 }}>
            &copy; 2025 Software Heroes, s.r.o.
          </footer>
        </HashRouter>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

const AppContent = ({
  t,
  onLanguageChange,
  token,
  userInfo,
  showMessage,
  serverUrl,
  ...rest
}) => {
  const location = useLocation();
  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        {t("title")}
      </Typography>
      <Box
        display="flex"
        justifyContent="flex-end"
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        <Button onClick={() => onLanguageChange("en")}>EN</Button>
        <Button onClick={() => onLanguageChange("cs")}>CZ</Button>
        {/*<Button
          component={Link}
          to="/guide"
          variant={location.pathname === "/guide" ? "contained" : "text"}
        >
          {t("guideNavLabel")}
        </Button>*/}
        {token && (userInfo?.role === "admin" || userInfo?.role === "manager") && (
          <Button
            component={Link}
            to={location.pathname === "/admin" ? "/" : "/admin"}
            variant="outlined"
          >
            {location.pathname === "/admin"
              ? t("adminNavHome")
              : t("adminNavDashboard")}
          </Button>
        )}
      </Box>
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              token={token}
              showMessage={showMessage}
              serverUrl={serverUrl}
              {...rest}
            />
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute
              t={t}
              token={token}
              userInfo={userInfo}
              showMessage={showMessage}
              serverUrl={serverUrl}
              {...rest}
            />
          }
        />
        <Route
          path="/guide"
          element={<UserGuide />}
        />
        <Route
          path="/reset-password"
          element={
            <ResetPasswordPage
              serverUrl={serverUrl}
              showMessage={showMessage}
            />
          }
        />
        <Route
          path="/verify-email"
          element={<VerifyEmailPage serverUrl={serverUrl} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Container>
  );
};

const HomePage = ({
  token,
  setToken,
  devices,
  setDevices,
  setSelectedDevice,
  setSelectedDeviceName,
  setOpenModal,
  handleLogout,
  showMessage,
  serverUrl,
  publicSettings,
}) => {
  if (!token) {
    return (
      <LoginRegisterForm
        setToken={setToken}
        showMessage={showMessage}
        serverUrl={serverUrl}
        requireEmailVerification={
          publicSettings?.require_email_verification !== false
        }
        ldapEnabled={publicSettings?.ldap_enabled === true}
      />
    );
  }

  return (
    <DeviceList
      token={token}
      devices={devices}
      setDevices={setDevices}
      setSelectedDevice={setSelectedDevice}
      setSelectedDeviceName={setSelectedDeviceName}
      setOpenModal={setOpenModal}
      logout={handleLogout}
      showMessage={showMessage}
      serverUrl={serverUrl}
    />
  );
};

const AdminRoute = ({
  t,
  token,
  userInfo,
  showMessage,
  serverUrl,
  publicSettings,
}) => {
  if (!token) {
    return <Navigate to="/" replace />;
  }
  if (userInfo?.role !== "admin" && userInfo?.role !== "manager") {
    return (
      <Typography variant="body1" color="error">
        {t("adminAccessDenied")}
      </Typography>
    );
  }
  return (
    <AdminDeviceDashboard
      token={token}
      serverUrl={serverUrl}
      showMessage={showMessage}
      userRole={userInfo?.role || ""}
      ldapEnabled={Boolean(publicSettings?.ldap_enabled)}
    />
  );
};

export default App;
